import {
  ADD_STORAGE_TO_SCREEN_OUTPUTS,
  ADD_UNIT,
  EDIT_FLOW_PROPERTIES,
  EDIT_LAYOUT,
  EDIT_LAYOUTS,
  EDIT_MULTIPLE_UNIT_LOCATIONS,
  EDIT_PORT,
  EDIT_UNIT_FLOW,
  EDIT_UNIT_INFO,
  INSERT_ON_CONNECTION,
  REMOVE_UNIT,
  REPLACE_UNIT,
  SET_UNIT_VALUE,
  TOGGLE_MIRRORING,
} from './types';
import type {
  ChamberAnalysisResultInterface,
  Group,
  NodeGroupInterface,
  PanelMapInterface,
  PanelType,
  PortsMapInterface,
  UnitInfoInterface,
  UnitInterface,
} from './types';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import {
  ADD_CONNECTION,
  EDIT_CONNECTION,
  REMOVE_CONNECTION,
  SET_CONNECTION_COLOR,
  UPDATE_CONNECTION_POINTS,
} from 'store/connections/types';
import type {
  ConnectionInterface,
  ConnectionStateInterface,
} from 'store/connections/types';
import { setOpenPanel } from 'store/layout/reducer';
import { clearDesignerState } from 'store/settings/reducer';
import { setSimulationResult } from 'store/simulation/actions';
import type { SimulationResultInterface } from 'store/simulation/types';
import { UnitTypes } from 'unitData/types';
import { findEmptyGroups } from 'util/groups';
import {
  isCrusherRiskAssessmentInfo,
  SELECTABLE_CRUSHER_RISK_FACTOR_INCLUSION_FLAG_MAP,
  type SelectableCrusherRiskFactorKey,
} from 'util/riskAssessment';
import type { PropertyPath } from 'lodash';
import { get, set } from 'lodash';
import type { ChamberAnalysisInterface } from 'store/chamberAnalysis/types';

export interface UnitReducerInterface {
  units: UnitInterface[];
  ports: PortsMapInterface;
  panels: PanelMapInterface;
  groups: NodeGroupInterface;
  simulationResults: { [unitKey: string]: SimulationResultInterface };
  chamberAnalysisResults: { [unitKey: string]: ChamberAnalysisResultInterface };
  activeUnit: string | null;
  activeUnitTitle?: string | null;
  activeUnitError: boolean;
  showFlowProperties: boolean;
  additionalActiveUnitError: boolean;
  selectedNodes: string[];
}
export interface UnitConnGroupReducerInterface {
  unitState: UnitReducerInterface;
  connectionState: ConnectionStateInterface;
}

export const initialUnitState = {
  units: [],
  ports: {},
  panels: {},
  groups: {},
  simulationResults: {},
  chamberAnalysisResults: {},
  activeUnit: null,
  activeUnitTitle: null,
  activeUnitError: false,
  showFlowProperties: false,
  additionalActiveUnitError: false,
  selectedNodes: [],
};

export const initialConnectionState = {
  connections: [],
};

export const initialState: UnitConnGroupReducerInterface = {
  unitState: initialUnitState,
  connectionState: initialConnectionState,
};

const mergePanelUpdates = (
  panels: PanelMapInterface,
  updates: PanelMapInterface,
) => {
  return Object.keys(panels).reduce((acc, key) => {
    return {
      ...acc,
      [key]: {
        ...panels[key],
        ...updates[key],
      },
    };
  }, updates);
};

export type SetUnitValueActionPayload = {
  unitKey: string;
  field: PropertyPath;
  value: unknown;
};

export type SetUnitValueAction = {
  type: typeof SET_UNIT_VALUE;
  payload: SetUnitValueActionPayload;
};

export function setUnitValueByPath(
  unitKey: string,
  field: PropertyPath,
  value: unknown,
): SetUnitValueAction {
  return {
    type: SET_UNIT_VALUE,
    payload: { unitKey, field, value },
  };
}

export function setUnitValue<T extends UnitInfoInterface>(
  unitKey: string,
  field: keyof T,
  value: T[keyof T],
): SetUnitValueAction {
  return {
    type: SET_UNIT_VALUE,
    payload: { unitKey, field, value },
  };
}

setUnitValue.type = SET_UNIT_VALUE;

const unitSlice = createSlice({
  name: 'unit',
  initialState,
  reducers: {
    setUnitData: (
      state,
      action: PayloadAction<{
        units: UnitInterface[];
        ports: PortsMapInterface;
        panels?: PanelMapInterface;
        groups?: NodeGroupInterface;
        simulationResults?: { [unitKey: string]: SimulationResultInterface };
        chamberAnalysisResults?: {
          [unitKey: string]: ChamberAnalysisResultInterface;
        };
      }>,
    ) => {
      state.unitState.units = action.payload.units;
      state.unitState.ports = action.payload.ports;
      if (action.payload.panels) {
        state.unitState.panels = action.payload.panels;
      }
      if (action.payload.groups) {
        state.unitState.groups = action.payload.groups;
      }
      if (action.payload.simulationResults) {
        state.unitState.simulationResults = action.payload.simulationResults;
      }
      if (action.payload.chamberAnalysisResults) {
        state.unitState.chamberAnalysisResults =
          action.payload.chamberAnalysisResults;
      }
    },
    setUnits: (state, action: PayloadAction<UnitInterface[]>) => {
      state.unitState.units = action.payload;
    },
    removeUnits: (state, action: PayloadAction<string[]>) => {
      state.unitState.units = state.unitState.units.filter(
        (u) => !action.payload.includes(u.key),
      );

      // Clear simulation results for removed units
      action.payload.forEach((key) => {
        delete state.unitState.simulationResults[key];
        delete state.unitState.chamberAnalysisResults[key];
      });

      state.connectionState.connections =
        state.connectionState.connections.filter(
          (c) =>
            !(action.payload.includes(c.from) || action.payload.includes(c.to)),
        );

      Object.values(state.unitState.groups).forEach((group) => {
        group.unitKeys = group.unitKeys.filter(
          (value) => !action.payload.includes(value),
        );
      });

      const emptyGroups = findEmptyGroups(
        state.unitState.units,
        state.unitState.groups,
      );

      emptyGroups.forEach((group) => {
        state.unitState.units = state.unitState.units.filter(
          (u) => u.key !== group.textBoxKey,
        );
        delete state.unitState.groups[group.key];
      });
    },
    setPorts: (state, action: PayloadAction<PortsMapInterface>) => {
      state.unitState.ports = action.payload;
    },
    updatePanels: (state, action: PayloadAction<PanelMapInterface>) => {
      state.unitState.panels = mergePanelUpdates(
        state.unitState.panels,
        action.payload,
      );
    },
    updatePanelsWithHistory: (
      state,
      action: PayloadAction<PanelMapInterface>,
    ) => {
      state.unitState.panels = mergePanelUpdates(
        state.unitState.panels,
        action.payload,
      );
    },
    addPanel: (
      state,
      action: PayloadAction<{ unitKey: string; type: PanelType; loc: string }>,
    ) => {
      const { loc, type, unitKey } = action.payload;
      state.unitState.panels[unitKey] = {
        ...state.unitState.panels[unitKey],
        [type]: { loc, type },
      };
    },
    removePanels: (state, action: PayloadAction<string[]>) => {
      state.unitState.panels = Object.entries(state.unitState.panels).reduce(
        (acc, [key, panels]) => {
          if (action.payload.includes(key)) {
            return acc;
          }
          return {
            ...acc,
            [key]: panels,
          };
        },
        {},
      );
    },
    updateUnits: (state, action: PayloadAction<UnitInterface[]>) => {
      const newUnits = action.payload;
      const oldUnits = state.unitState.units.filter(
        (u) => !newUnits.find((unit) => u.key === unit.key),
      );
      state.unitState.units = [...oldUnits, ...newUnits];
    },
    setActiveUnit: (state, action: PayloadAction<string | null>) => {
      if (
        !state.unitState.activeUnitError &&
        !state.unitState.additionalActiveUnitError
      ) {
        state.unitState.activeUnit = action.payload;
        state.unitState.activeUnitTitle = null;
        state.unitState.showFlowProperties = false;
      }
    },
    setActiveUnitTitle: (state, action: PayloadAction<string | null>) => {
      state.unitState.activeUnitTitle = action.payload;
    },
    setActiveUnitError: (state, action: PayloadAction<boolean>) => {
      state.unitState.activeUnitError = action.payload;
    },
    setAdditionalActiveUnitError: (state, action: PayloadAction<boolean>) => {
      state.unitState.additionalActiveUnitError = action.payload;
    },
    setShowFlowProperties: (state, action: PayloadAction<boolean>) => {
      state.unitState.showFlowProperties = action.payload;
    },
    hideNonMajorWarnings: (state, action: PayloadAction<boolean>) => {
      state.unitState.units = state.unitState.units.map((unit) => ({
        ...unit,
        layout: {
          ...unit.layout,
          hideNonMajorWarnings: action.payload,
        },
      }));
    },
    selectNode: (
      state,
      action: PayloadAction<{
        nodeKey: string;
        unitKey: string;
        multiSelect: boolean;
      }>,
    ) => {
      const { nodeKey, unitKey, multiSelect } = action.payload;
      if (!multiSelect) {
        state.unitState.activeUnit = unitKey;
        state.unitState.selectedNodes = [nodeKey];
      } else {
        if (state.unitState.selectedNodes.includes(nodeKey)) {
          state.unitState.selectedNodes = state.unitState.selectedNodes.filter(
            (n) => n !== nodeKey,
          );
          if (state.unitState.selectedNodes.length === 0) {
            state.unitState.activeUnit = null;
          }
        } else {
          state.unitState.selectedNodes.push(nodeKey);
          state.unitState.activeUnit = null;
        }
      }
    },
    setSelectedNodes: (state, action: PayloadAction<string[]>) => {
      state.unitState.selectedNodes = action.payload;
      state.unitState.activeUnit = null;
    },
    selectAllNodes: (state) => {
      const unitKeys = state.unitState.units
        .filter((u) => u.type !== UnitTypes.PRINT_WINDOW)
        .map((u) => u.key);
      const panelKeys = Object.entries(state.unitState.panels).flatMap(
        ([unitKey, panels]) =>
          Object.values(panels).map((p) => `${unitKey}-${p.type}`),
      );
      state.unitState.selectedNodes = [...unitKeys, ...panelKeys];
    },
    clearSelectedNodes: (state) => {
      state.unitState.selectedNodes = [];
    },
    setConnections(state, action: PayloadAction<ConnectionInterface[]>) {
      state.connectionState.connections = action.payload;
    },
    removeConnectionByFromPort(state, action: PayloadAction<string>) {
      const fromPortKey = action.payload;
      state.connectionState.connections =
        state.connectionState.connections.reduce(
          (acc: ConnectionInterface[], curr: ConnectionInterface) => {
            if (curr.fromPort === fromPortKey) {
              return acc;
            }
            return [...acc, curr];
          },
          [],
        );
    },
    removeConnectionByToPort(state, action: PayloadAction<string>) {
      const toPortKey = action.payload;
      state.connectionState.connections =
        state.connectionState.connections.reduce(
          (acc: ConnectionInterface[], curr: ConnectionInterface) => {
            if (curr.toPort === toPortKey) {
              return acc;
            }
            return [...acc, curr];
          },
          [],
        );
    },
    editGroup(state, action: PayloadAction<{ key: string; group: Group }>) {
      const { key, group } = action.payload;
      state.unitState.groups[key] = group;
    },
    editGroupWithHistory(
      state,
      action: PayloadAction<{ key: string; group: Group }>,
    ) {
      const { key, group } = action.payload;
      state.unitState.groups[key] = group;
    },
    deleteGroups(state, action: PayloadAction<string[]>) {
      action.payload.forEach((g) => {
        const group = state.unitState.groups[g];
        state.unitState.units = state.unitState.units.filter(
          (u) => u.key !== group.textBoxKey,
        );
        delete state.unitState.groups[g];
      });
    },
    deleteGroupsWithHistory(state, action: PayloadAction<string[]>) {
      action.payload.forEach((g) => {
        const group = state.unitState.groups[g];
        state.unitState.units = state.unitState.units.filter(
          (u) => u.key !== group.textBoxKey,
        );
        delete state.unitState.groups[g];
      });
    },
    setSelectableCrusherRiskFactorIncluded: (
      state,
      action: PayloadAction<{
        unitKey: string;
        factorKey: SelectableCrusherRiskFactorKey;
        included: boolean;
      }>,
    ) => {
      const { unitKey, factorKey, included } = action.payload;
      const simulationResult = state.unitState.simulationResults[unitKey];
      const riskAssessmentInfo = simulationResult?.riskAssessmentInfo;

      if (
        !simulationResult ||
        !riskAssessmentInfo ||
        !isCrusherRiskAssessmentInfo(riskAssessmentInfo)
      ) {
        return;
      }

      const inclusionKey =
        SELECTABLE_CRUSHER_RISK_FACTOR_INCLUSION_FLAG_MAP[factorKey];

      state.unitState.simulationResults[unitKey] = {
        ...simulationResult,
        riskAssessmentInfo: {
          ...riskAssessmentInfo,
          [inclusionKey]: included,
        },
      };
    },
    setSimulationResults: (
      state,
      action: PayloadAction<{ [unitKey: string]: SimulationResultInterface }>,
    ) => {
      state.unitState.simulationResults = {
        ...state.unitState.simulationResults,
        ...action.payload,
      };
    },
    clearSimulationResults: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((key) => {
        delete state.unitState.simulationResults[key];
      });
    },
    setChamberAnalysisResults: (
      state,
      action: PayloadAction<{
        [unitKey: string]: ChamberAnalysisResultInterface;
      }>,
    ) => {
      state.unitState.chamberAnalysisResults = {
        ...state.unitState.chamberAnalysisResults,
        ...action.payload,
      };
    },
    setChamberAnalysisReference: (
      state,
      action: PayloadAction<{
        unitKey: string;
        reference: ChamberAnalysisInterface | null;
      }>,
    ) => {
      const { unitKey, reference } = action.payload;
      const existing = state.unitState.chamberAnalysisResults[unitKey];
      if (existing) {
        state.unitState.chamberAnalysisResults[unitKey] = {
          ...existing,
          reference,
        };
      }
    },
    setChamberAnalysisDisplaySettings: (
      state,
      action: PayloadAction<{
        unitKey: string;
        displaySettings: {
          showFeedSize: boolean;
          showCompressionZones: boolean;
          autoSimulate: boolean;
        };
      }>,
    ) => {
      const { unitKey, displaySettings } = action.payload;
      const existing = state.unitState.chamberAnalysisResults[unitKey];
      if (existing) {
        state.unitState.chamberAnalysisResults[unitKey] = {
          ...existing,
          displaySettings,
        };
      }
    },
  },

  extraReducers: (builder) => {
    /**
     * The setUnitValue action needs to be in extra reducers
     * to ensure type safety.
     */
    builder.addCase(setUnitValue, (state, action) => {
      const { unitKey, field, value } = action.payload;
      const unit = state.unitState.units.find((u) => u.key === unitKey);
      if (unit && get(unit.unitInfo, field) !== value) {
        set(unit.unitInfo, field, value);
        // unit.unitInfo[field] = value;
        state.unitState.activeUnitError = false;
        state.unitState.additionalActiveUnitError = false;
      }
    });
    builder.addCase(clearDesignerState, () => {
      return initialState;
    });
    builder.addCase(setOpenPanel, (state, action) => {
      if (action.payload !== null) {
        state.unitState.activeUnit = null;
      }
    });
    builder.addCase(setSimulationResult, (state, action) => {
      state.connectionState.connections =
        state.connectionState.connections.reduce(
          (acc: ConnectionInterface[], curr: ConnectionInterface) => {
            const relatedPort = Object.values(action.payload.ports).find(
              (port) => port.key === curr.fromPort,
            );
            if (relatedPort) {
              return [
                ...acc,
                {
                  ...curr,
                  outputFlow: relatedPort.outputFlow.totalMass,
                },
              ];
            }

            return [...acc, curr];
          },
          [],
        );
    });
  },
});

export const {
  setUnitData,
  setUnits,
  setPorts,
  addPanel,
  updateUnits,
  updatePanels,
  updatePanelsWithHistory,
  setActiveUnit,
  setActiveUnitTitle,
  setActiveUnitError,
  setAdditionalActiveUnitError,
  setShowFlowProperties,
  hideNonMajorWarnings,
  removePanels,
  selectNode,
  selectAllNodes,
  clearSelectedNodes,
  setSelectedNodes,
  removeConnectionByFromPort,
  removeConnectionByToPort,
  setConnections,
  removeUnits,
  editGroup,
  editGroupWithHistory,
  deleteGroups,
  deleteGroupsWithHistory,
  setSelectableCrusherRiskFactorIncluded,
  setSimulationResults,
  clearSimulationResults,
  setChamberAnalysisResults,
  setChamberAnalysisReference,
  setChamberAnalysisDisplaySettings,
} = unitSlice.actions;

export const undoableActions = [
  ADD_CONNECTION,
  EDIT_CONNECTION,
  REMOVE_CONNECTION,
  'REMOVE_CONNECTION_BY_FROM_PORT',
  'REMOVE_CONNECTION_BY_TO_PORT',
  UPDATE_CONNECTION_POINTS,
  SET_CONNECTION_COLOR,
  ADD_UNIT,
  EDIT_FLOW_PROPERTIES,
  EDIT_LAYOUT,
  EDIT_LAYOUTS,
  EDIT_MULTIPLE_UNIT_LOCATIONS,
  EDIT_PORT,
  EDIT_UNIT_FLOW,
  EDIT_UNIT_INFO,
  REMOVE_UNIT,
  REPLACE_UNIT,
  SET_UNIT_VALUE,
  TOGGLE_MIRRORING,
  updatePanelsWithHistory.type,
  editGroupWithHistory.type,
  deleteGroupsWithHistory.type,
  INSERT_ON_CONNECTION,
  ADD_STORAGE_TO_SCREEN_OUTPUTS,
  setChamberAnalysisReference.type,
];

export const unitReducer = unitSlice.reducer;
